import { motion } from 'framer-motion';
import { useState } from 'react';
import ReactSwitch from 'react-switch';
import browserHistory from '~/browserHistory';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Column } from '~/commonComponents';
import Button from '~/commonComponents/Button';
import { ModalOrder, useModal } from '~/components/ModalQueue';
import { useAcceptGoTuTerms } from '~/domain/AcceptGoTuTerms';
import { useAcceptPSATerms } from '~/domain/AcceptPSATerms';
import { useGetSelectedOffice } from '~/domain/SelectedOffice';
import { useOfficeTnCConsent } from '~/hooks/useOfficeTnCConsent';
import { useUserSelector } from '~/hooks/useUserSelector';
import documentTextIcon from '~/images/document-text-white.svg';
import { routes } from '~/routes';
import { getPopupValues } from './utils';

export function AcceptTermsPopup() {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const user = useUserSelector();
  const {
    isNeedOldTnC,
    isNeedNewTnC,
    isOnTnCPage,
    isLoading: isLoadingGetNewTnc,
    shouldShow: shouldShowNewTnc,
    consentContent,
    isActive: isActiveNewTnc
  } = useOfficeTnCConsent();
  const popupValues = getPopupValues(user, isNeedOldTnC, isNeedNewTnC);
  const acceptPSATermsMutation = useAcceptPSATerms();
  const acceptGoTuTermsMutation = useAcceptGoTuTerms();
  const mutateGetSelectedOffice = useGetSelectedOffice(user?.id);
  const isLoading =
    acceptPSATermsMutation.isPending ||
    acceptGoTuTermsMutation.isPending ||
    mutateGetSelectedOffice.isPending;

  const handleButtonClick = async () => {
    if (!popupValues) return;

    switch (popupValues.type) {
      case 'psa':
        await acceptPSATermsMutation.mutateAsync({ officeId: user?.id });
        break;
      case 'gotuTerms':
        await acceptGoTuTermsMutation.mutateAsync({ officeId: user?.id });
        break;
      case 'both':
        await Promise.all([
          acceptGoTuTermsMutation.mutateAsync({ officeId: user?.id }),
          acceptPSATermsMutation.mutateAsync({ officeId: user?.id }),
        ]);
        break;
      default:
        break;
    }
    await mutateGetSelectedOffice.mutateAsync();
  };

  const shouldShow = !!popupValues && !(isNeedOldTnC && user?.office_info?.psaTerms?.accepted);

  const { isActive } = useModal({
    id: ModalOrder.ACCEPT_TERMS_POPUP,
    condition: shouldShow && !isOnTnCPage,
  });

  if (isLoadingGetNewTnc) {
    return null;
  }

  if (shouldShowNewTnc && isActiveNewTnc) {
    return (
      <button type="button" style={{ all: 'unset' }}>
        <div
          className="modal"
          data-cy="office-tnc-consent-modal"
          data-testid="office-tnc-consent-modal"
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
          }}
        >
          <motion.div
            data-cy="office-tnc-consent-popup"
            data-testid="office-tnc-consent-popup"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            onClick={(event) => event.stopPropagation()}
          >
            <Column
              alignItems="center"
              width={500}
              maxHeight="90vh"
              backgroundColor={colors.shades[0]}
              borderRadius={12}
              paddingLeft={35}
              paddingRight={35}
              paddingTop={50}
              paddingBottom={50}
              overflow="auto"
              boxShadow="0px 2px 3px 0px rgba(0,0,0,0.15), 0px 6px 10px 2px rgba(0,0,0,0.15)"
            >
              <Typography as="span" bold size="body_large" color={colors.neutral[600]} textAlign="center" style={{ marginBottom: 16 }}>
                {consentContent?.header}
              </Typography>

              <Typography as="span" size="body_large" color={colors.neutral[600]} textAlign="center" style={{ marginBottom: 32 }}>
                {consentContent?.body}
              </Typography>

              <Button
                type="button"
                Icon={documentTextIcon}
                style={{ width: 'auto', paddingLeft: 26, paddingRight: 26, marginTop: 0 }}
                text="Click here to view"
                size="small"
                dataCy="office-tnc-accept-button"
                onClick={() => browserHistory.push(routes.termsAndConditions)}
              />
            </Column>
          </motion.div>
        </div>
      </button>
    );
  }

  if (!shouldShow) {
    return null;
  }

  if (!isActive) {
    return null;
  }

  return (
    <button type="button" style={{ all: 'unset' }}>
      <div
        className="modal"
        data-cy="accept-terms-modal"
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
        }}
      >
        <motion.div
          data-cy="accept-terms-popup"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            width: '28%',
            maxWidth: 700,
            maxHeight: '90%',
            backgroundColor: colors.shades[0],
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 14,
            padding: 35,
            paddingBottom: 48,
            overflowY: 'auto',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {popupValues.image && (
            <img
              src={popupValues.image}
              style={{ width: 220, marginBottom: 16 }}
              alt="welcome_illustration"
            />
          )}

          <h1
            style={{
              fontFamily: 'Nunito',
              fontWeight: 'bold',
              fontSize: 18,
              color: colors.neutral[600],
            }}
          >
            {popupValues.title}
          </h1>
          <p
            style={{
              fontFamily: 'Nunito',
              fontSize: 16,
              color: colors.neutral[500],
              margin: 0,
              marginBottom: 32,
              textAlign: 'center',
            }}
          >
            {popupValues.description}
          </p>

          {popupValues.switchText && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 32,
                gap: 16,
              }}
            >
              <ReactSwitch
                checked={acceptedTerms}
                onChange={() => setAcceptedTerms(!acceptedTerms)}
                onColor={colors.secondary[500]}
                onHandleColor="#fff"
                handleDiameter={20}
                uncheckedIcon={false}
                checkedIcon={false}
                offColor={colors.neutral[100]}
                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
                activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                height={25}
                width={50}
                className="react-switch"
                id="material-switch"
              />
              <p className="global_font" style={{ margin: 0, color: colors.neutral[500] }}>
                {popupValues.switchText}
              </p>
            </div>
          )}

          <Button
            type="button"
            style={{
              width: 150,
            }}
            disabled={popupValues.switchText && !acceptedTerms}
            onClick={handleButtonClick}
            text={popupValues.buttonText}
            isLoading={isLoading}
            dataCy="accept-terms-button"
          />
        </motion.div>
      </div>
    </button>
  );
}
